// packages/auth/src/refresh.ts
import { getAuthToken, getRefreshToken, setAuthToken, setRefreshToken, clearAuthToken, clearRefreshToken, } from './token';
const LOCK_KEY = 'refresh_lock';
// LOCK_TTL_MS DEBE ser > REFRESH_FETCH_TIMEOUT_MS: un fetch vivo nunca pierde su lock,
// y una pestaña que crasheó se recupera tras este TTL.
const LOCK_TTL_MS = 20000; // un lock más viejo que esto se considera huérfano
const REFRESH_FETCH_TIMEOUT_MS = 15000; // abortamos el fetch si tarda más
const WAIT_TIMEOUT_MS = 22000; // un waiter debe poder sobrevivir al TTL de un holder caído y tomar el relevo
const WAIT_POLL_MS = 60;
// Stamp único por pestaña: localStorage no tiene CAS, así que verificamos propiedad.
const TAB_STAMP = `${Date.now().toString(36)}.${Math.random().toString(36).slice(2)}`;
let myLock = null;
// Lock por pestaña: dedupe de llamadas concurrentes dentro del MISMO documento.
let inFlight = null;
export function __resetRefreshLock() {
    inFlight = null;
}
export function __resetCrossTabLock() {
    try {
        localStorage.removeItem(LOCK_KEY);
    }
    catch { /* noop */ }
}
function lockTs() {
    const v = localStorage.getItem(LOCK_KEY);
    // El valor es "<ts>:<stamp>"; parseInt para en ':', así que el prefijo entero sigue siendo el ts.
    const n = v ? parseInt(v, 10) : 0;
    return Number.isFinite(n) ? n : 0;
}
function tryAcquireLock() {
    const ts = lockTs();
    if (ts && Date.now() - ts < LOCK_TTL_MS)
        return false; // peer con lock fresco
    // Borrow-and-verify: escribimos nuestro stamp y comprobamos que ganamos el write.
    const stamp = `${Date.now()}:${TAB_STAMP}`;
    localStorage.setItem(LOCK_KEY, stamp);
    if (localStorage.getItem(LOCK_KEY) !== stamp)
        return false; // otra pestaña ganó la carrera
    myLock = stamp;
    return true;
}
function releaseLock() {
    try {
        // Solo soltamos si seguimos siendo el dueño (no pisar a un peer que tomó el relevo).
        if (localStorage.getItem(LOCK_KEY) === myLock)
            localStorage.removeItem(LOCK_KEY);
    }
    catch { /* noop */ }
    myLock = null;
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// Espera a que otra pestaña publique un access token distinto a `prev`.
// Devuelve el token nuevo, o null si expira el tiempo / se libera el lock sin cambio.
async function waitForPeerToken(prev) {
    const deadline = Date.now() + WAIT_TIMEOUT_MS;
    while (Date.now() < deadline) {
        const t = getAuthToken();
        if (t && t !== prev)
            return t;
        if (!lockTs())
            break; // el peer soltó el lock sin publicar token nuevo (falló)
        await sleep(WAIT_POLL_MS);
    }
    const t = getAuthToken();
    return t && t !== prev ? t : null;
}
async function doRefresh(opts) {
    const failed = opts.failedToken ?? getAuthToken();
    // 1) ¿Otra pestaña ya refrescó? El access token ya es distinto al que falló.
    const current = getAuthToken();
    if (current && current !== failed)
        return current;
    // 2) Coordinación cross-tab: si un peer tiene el lock, esperamos su token.
    if (!tryAcquireLock()) {
        const peerToken = await waitForPeerToken(failed);
        if (peerToken)
            return peerToken;
        // timeout: el peer no publicó token; intentamos nosotros (re-adquirimos).
        tryAcquireLock();
    }
    try {
        // Re-chequear tras adquirir el lock (puede haber cambiado mientras tanto).
        const cur2 = getAuthToken();
        if (cur2 && cur2 !== failed)
            return cur2;
        const refresh = getRefreshToken();
        if (!refresh)
            throw new Error('no_refresh_token');
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), REFRESH_FETCH_TIMEOUT_MS);
        let res;
        try {
            res = await fetch(`${opts.backendUrl.replace(/\/$/, '')}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refresh }),
                signal: ctrl.signal,
            });
        }
        catch (e) {
            // Error de red / abort por timeout: NO limpiamos tokens (es transitorio). Re-lanzamos.
            throw e;
        }
        finally {
            clearTimeout(timer);
        }
        if (!res.ok) {
            clearAuthToken();
            clearRefreshToken();
            throw new Error('refresh_failed_' + res.status);
        }
        const data = await res.json();
        if (data.token)
            setAuthToken(data.token);
        // COMPARE-AND-SET: solo publicamos el refresh nuevo si en localStorage SIGUE
        // estando el que enviamos. Si otra pestaña ya publicó una generación más nueva,
        // escribir la nuestra sería una REGRESIÓN: dejaríamos guardado un secreto viejo
        // que, al caducar su ventana de gracia, el backend clasificaría como reuso y
        // borraría la familia entera (sesión muerta, "Authorization header missing").
        if (data.refresh_token && data.refresh_token !== refresh) {
            if (getRefreshToken() === refresh)
                setRefreshToken(data.refresh_token);
        }
        return data.token;
    }
    finally {
        releaseLock();
    }
}
export function refreshAccessToken(opts) {
    if (inFlight)
        return inFlight;
    inFlight = doRefresh(opts).finally(() => {
        inFlight = null;
    });
    return inFlight;
}
